import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { AuthContext } from "../../AuthProvider";
import { useCan } from "../../authz/useCan";
import { FaClipboardList } from "react-icons/fa";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../../components/lds/LdsOverview";
import {
  getChildCount,
  getClassroomId,
  getClassroomName,
  listMyClassrooms,
  type MyClassroom,
} from "../../services/myEnvironmentService";
import { translateClassName } from "../../utils/classroomUtils";
import {
  OutingActionsButton,
  OutingModals,
  OutingOverviewCard,
} from "../../pages/classroom-details/OutingControls";
import { useOutings } from "../../pages/classroom-details/useOutings";
import http from "../../api/apiClient";
import { API_PATH as SCHOOL_CALENDAR_API_PATH } from "../../pages/schoolCalendar/constants";
import {
  entityId,
  eventClassroomNames,
  eventClassroomUuids,
  eventScope,
  normalizeList,
} from "../../pages/schoolCalendar/utils";
import type { SchoolCalendarEvent } from "../../pages/schoolCalendar/types";
import { LdsBadge } from "../../components/lds/LdsBadge";
import {
  formatTimeOnly,
  isSameCalendarDay,
  safeLocale,
  toDateValue,
} from "../../pages/Dashboard/utils/dashboardDate.utils";
import { isPersonalCalendarEvent } from "../../pages/Dashboard/utils/dashboardUpcoming.utils";

const outingsStyles = `
  @keyframes myOutingsEnter {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .my-outings-enter {
    animation: myOutingsEnter 420ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .my-outings-enter { animation: none !important; }
  }
`;

const getYear = (value: unknown) => {
  if (!value) return null;
  const date = new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

const getClassroomSchoolYearLabel = (classroom: MyClassroom) => {
  const anyClassroom = classroom as any;

  const directLabel =
    anyClassroom.schoolYearLabel ||
    anyClassroom.schoolYearName ||
    anyClassroom.yearLabel ||
    anyClassroom.yearName ||
    anyClassroom.schoolYear?.label ||
    anyClassroom.schoolYear?.name;

  if (directLabel) return directLabel;

  const startDate =
    anyClassroom.schoolYear?.startDate ||
    anyClassroom.startDate ||
    anyClassroom.yearStartDate;

  const endDate =
    anyClassroom.schoolYear?.endDate ||
    anyClassroom.endDate ||
    anyClassroom.yearEndDate;

  const startYear = getYear(startDate);
  const endYear = getYear(endDate);

  if (startYear != null && endYear != null) {
    return `${startYear} - ${endYear}`;
  }

  return null;
};

const getTranslatedClassroomName = (classroom: MyClassroom) => {
  return translateClassName(classroom) || getClassroomName(classroom);
};

const getClassroomDropdownLabel = (classroom: MyClassroom) => {
  const name = getTranslatedClassroomName(classroom);
  const schoolYear = getClassroomSchoolYearLabel(classroom);

  return schoolYear ? `${name} · ${schoolYear}` : name;
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const formatAgendaWhen = (event: SchoolCalendarEvent, locale: string, allDayLabel: string) => {
  const start = toDateValue(event.startDate ?? null);
  const end = toDateValue(event.endDate ?? null);
  if (!start) return "";

  if (end && !isSameCalendarDay(start, end)) {
    const short = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
    return `${short.format(start)} – ${short.format(end)}`;
  }

  const day = capitalize(
    new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(start)
  );
  if (event.allDay) return `${day} · ${allDayLabel}`;

  const startTime = formatTimeOnly(start, locale);
  const endTime = end ? formatTimeOnly(end, locale) : "";
  return `${day} · ${startTime}${endTime && endTime !== startTime ? ` – ${endTime}` : ""}`;
};

const cardClassName =
  "my-outings-enter rounded-[28px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_14px_35px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950";

const Outings: React.FC = () => {
  const { keycloak } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const locale = safeLocale(i18n?.language);

  const canManageOutings = useCan("outing.info", "manage");
  const canReadOutingsPermission = useCan("outing.info", "read");
  const canViewOutingsReport = useCan("reports.outings", "view");
  const canViewAgendaActivities = useCan("my.agenda-activities", "view");
  const canReadClassroomCalendar = useCan("classroom.calendar", "read");
  const canManageClassroomCalendar = useCan("classroom.calendar", "manage");
  const canReadOutings = canReadOutingsPermission || canManageOutings || canViewOutingsReport;
  const canViewClassroomCalendarItems = canReadClassroomCalendar || canManageClassroomCalendar;
  // Mirrors the read check of GET /school-calendar-events; the backend stays authoritative.
  const canLoadCalendar = canViewAgendaActivities || canViewClassroomCalendarItems;
  const canViewAgendaActivitiesPage =
    canViewAgendaActivities || canViewClassroomCalendarItems || canReadOutings;

  const [classrooms, setClassrooms] = useState<MyClassroom[]>([]);
  // "" means "All classes": the classroom is a filter, never a prerequisite.
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [events, setEvents] = useState<SchoolCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const sortedClassrooms = useMemo(
    () =>
      [...classrooms].sort((a, b) => {
        const yearCompare = (getClassroomSchoolYearLabel(b) || "").localeCompare(
          getClassroomSchoolYearLabel(a) || ""
        );
        if (yearCompare !== 0) return yearCompare;
        return getTranslatedClassroomName(a).localeCompare(getTranslatedClassroomName(b), undefined, {
          numeric: true,
        });
      }),
    [classrooms]
  );

  const selectedClassroom = useMemo(
    () => classrooms.find((classroom) => getClassroomId(classroom) === selectedClassroomId) ?? null,
    [classrooms, selectedClassroomId]
  );

  const outings = useOutings({
    classroomId: selectedClassroomId,
    canRead: canReadOutings,
    canManage: canManageOutings,
    t,
  });

  useEffect(() => {
    if (!keycloak?.token) return undefined;
    let cancelled = false;

    const load = async () => {
      setRefreshing(true);
      // A user without classrooms (or without access to them) still gets the school-wide items.
      const [classroomResult, eventResult] = await Promise.allSettled([
        listMyClassrooms(),
        canLoadCalendar ? http.get(SCHOOL_CALENDAR_API_PATH) : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;

      setClassrooms(
        classroomResult.status === "fulfilled" && Array.isArray(classroomResult.value)
          ? classroomResult.value
          : []
      );
      setEvents(
        eventResult.status === "fulfilled"
          ? normalizeList<SchoolCalendarEvent>(eventResult.value.data)
          : []
      );
      setLoadFailed(eventResult.status === "rejected");
      setLoading(false);
      setRefreshing(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [keycloak?.token, canLoadCalendar, reloadKey]);

  useEffect(() => {
    if (!selectedClassroomId || !canReadOutings) return;
    outings.fetchOutingOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassroomId, canReadOutings]);

  // Same visibility rule as the dashboard "Upcoming" panel: school-wide items plus the user's own classes.
  // ponytail: loads the full calendar and filters client-side like the dashboard; pass from/schoolYearUuid if it grows.
  const visibleEvents = useMemo(() => {
    const classroomIds = new Set<string>(classrooms.map(getClassroomId).filter(Boolean));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOf = (event: SchoolCalendarEvent) => toDateValue(event.startDate ?? null)?.getTime() ?? Number.NaN;

    return events
      .filter((event) => isPersonalCalendarEvent(event, classroomIds))
      .filter((event) => {
        const start = startOf(event);
        const end = toDateValue(event.endDate ?? null)?.getTime() ?? start;
        return Number.isFinite(start) && Math.max(start, end) >= today.getTime();
      })
      .sort((a, b) => startOf(a) - startOf(b));
  }, [classrooms, events]);

  const filteredEvents = useMemo(
    () =>
      selectedClassroomId
        ? visibleEvents.filter(
            (event) =>
              eventScope(event) === "SCHOOL" || eventClassroomUuids(event).includes(selectedClassroomId)
          )
        : visibleEvents,
    [selectedClassroomId, visibleEvents]
  );

  const monthGroups = useMemo(() => {
    const monthFormat = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
    const groups: { label: string; events: SchoolCalendarEvent[] }[] = [];
    filteredEvents.forEach((event) => {
      const label = monthFormat.format(toDateValue(event.startDate ?? null) as Date);
      const last = groups[groups.length - 1];
      if (last?.label === label) last.events.push(event);
      else groups.push({ label, events: [event] });
    });
    return groups;
  }, [filteredEvents, locale]);

  const classroomLookup = useMemo(
    () => new Map(classrooms.map((classroom) => [getClassroomId(classroom), classroom])),
    [classrooms]
  );

  const hero = (
    <LdsOverviewHero
      icon={FaClipboardList}
      eyebrow={t("myEnvironment.eyebrow", { defaultValue: "Personal workspace" })}
      title={t("myEnvironment.outings.title", { defaultValue: "Agenda & activities" })}
      description={t("myEnvironment.outings.description", {
        defaultValue: "View calendar items, activities, outings and parent consent status.",
      })}
    />
  );

  if (!canViewAgendaActivitiesPage) {
    return (
      <ErrorPage
        status={403}
        message={t("error.403.message", {
          defaultValue: "You do not have permission to view this page.",
        })}
      />
    );
  }

  if (loading) {
    return (
      <LdsOverviewPage styles={<style>{outingsStyles}</style>}>
        {hero}
        <div className="my-outings-enter flex justify-center py-10">
          <Spinner />
        </div>
      </LdsOverviewPage>
    );
  }

  const emptyMessage = loadFailed
    ? t("myEnvironment.outings.agendaLoadFailed", { defaultValue: "Unable to load the agenda." })
    : visibleEvents.length === 0
    ? t("myEnvironment.outings.noAgendaItems", { defaultValue: "No upcoming agenda items found." })
    : filteredEvents.length === 0
    ? t("myEnvironment.outings.noAgendaItemsForClassroom", {
        defaultValue: "No agenda items found for this class.",
      })
    : null;

  return (
    <LdsOverviewPage styles={<style>{outingsStyles}</style>}>
      {hero}

      <div className="space-y-6">
        <section className={cardClassName}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full sm:max-w-sm">
              <label
                htmlFor="agenda-classroom-filter"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {t("myEnvironment.outings.classroom", { defaultValue: "Classroom" })}
              </label>
              <select
                id="agenda-classroom-filter"
                value={selectedClassroomId}
                onChange={(event) => setSelectedClassroomId(event.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition hover:bg-slate-50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
              >
                <option value="">
                  {t("myEnvironment.outings.allClassrooms", { defaultValue: "All classes" })}
                </option>
                {sortedClassrooms.map((classroom) => (
                  <option key={getClassroomId(classroom)} value={getClassroomId(classroom)}>
                    {getClassroomDropdownLabel(classroom)}
                  </option>
                ))}
              </select>
              {classrooms.length === 0 && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t("myEnvironment.outings.noClassroomsToFilter", {
                    defaultValue: "No classes available to filter by.",
                  })}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {selectedClassroom && (
                <>
                  <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    {t("myEnvironment.outings.childrenCount", {
                      defaultValue: "{{count}} children",
                      count: getChildCount(selectedClassroom),
                    })}
                  </span>

                  <OutingActionsButton
                    outings={outings}
                    classroomId={selectedClassroomId}
                    t={t}
                    canManage={canManageOutings}
                  />
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setReloadKey((value) => value + 1);
                  if (selectedClassroomId && canReadOutings) outings.fetchOutingOverview();
                }}
                disabled={refreshing}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {refreshing
                  ? t("common.loading", { defaultValue: "Loading..." })
                  : t("common.refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
          </div>
        </section>

        {emptyMessage ? (
          <section className="my-outings-enter rounded-[28px] border border-dashed border-slate-300 bg-white/90 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            {emptyMessage}
          </section>
        ) : (
          <section className={cardClassName}>
            <div className="space-y-6">
              {monthGroups.map((group) => (
                <div key={group.label}>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {group.label}
                  </h2>
                  <ul className="mt-2 divide-y divide-slate-200/80 dark:divide-slate-800">
                    {group.events.map((event) => {
                      const schoolWide = eventScope(event) === "SCHOOL";
                      const classNames: string[] = schoolWide ? [] : eventClassroomNames(event, classroomLookup);
                      return (
                        <li
                          key={String(entityId(event) || `${event.title || "calendar"}-${event.startDate || ""}`)}
                          className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 dark:text-white">
                              {event.title || t("schoolCalendar.untitled", { defaultValue: "Untitled" })}
                            </p>
                            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
                              {formatAgendaWhen(
                                event,
                                locale,
                                t("dashboard.all_day", { defaultValue: "All day" })
                              )}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:shrink-0 sm:justify-end">
                            {schoolWide ? (
                              <LdsBadge className="!px-2 !py-0.5">
                                {t("myEnvironment.outings.wholeSchool", { defaultValue: "Whole school" })}
                              </LdsBadge>
                            ) : (
                              classNames.map((name) => (
                                <LdsBadge key={name} tone="info" className="!px-2 !py-0.5">
                                  {name}
                                </LdsBadge>
                              ))
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {selectedClassroom && outings.sortedOutings.length > 0 && (
          <section className={cardClassName}>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {t("classroomDetails.actions.outingsForms", { defaultValue: "Outings & forms" })}
            </h2>
            <OutingOverviewCard outings={outings} t={t} initialVisibleCount={5} />
          </section>
        )}

        <OutingModals outings={outings} t={t} canManage={canManageOutings} />
      </div>
    </LdsOverviewPage>
  );
};

export default Outings;
